import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Modal from 'component/utils/Modal'
import moment from 'moment'
import useScrollShadow from 'utils/hooks/useScrollShadow'

const ViewModal = ({
  closeModal,
  translate,
  param,
  openEditModal
}) => {

  if (!param) return null

  const {
    branches = [],
    branchNamesGrouped = [],
    dynamic,
    dsCard,
    dtStart,
    dtEnd,
    parcelRangeList = [],
  } = param || {}

  const renderBranchNames = () => {
    if (branchNamesGrouped?.length >= 5) {
      return translate('price.banner.all')
    }

    const regionNames = branchNamesGrouped || [];
    
    const individualBranches = (branches || [])
      .filter(b => !regionNames.includes(b.dsRegion))
      .map(({ idBranch, dsBranch }) => {
        const branchCode = idBranch.replace(/^00/, '')
        const cleanedName = dsBranch
          .replace(/loja\s*/i, '')
          .trim()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
        return `${branchCode} - ${cleanedName}`
      });
    
    const allItems = [...regionNames, ...individualBranches];
    
    if (allItems.length > 0) {
      return allItems.join('\n');
    }
    
    return '-';
  }

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-'

    const normalized = typeof value === 'string' ? value.replace(',', '.') : value

    const number = parseFloat(normalized)

    if (Number.isNaN(number)) return '-'

    return number.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return moment(date).format('DD/MM/YYYY')
  }

  const capitalizeFirst = (text) => {
    if (!text) return '-'
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
  }

  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const containerRef = useRef(null);
  const storesScrollRef = useScrollShadow([branches, branchNamesGrouped]);

  useEffect(() => {
    const container = containerRef.current;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;

      const atTop = scrollTop === 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

      if (headerRef.current) {
        headerRef.current.classList.toggle('scrolled', !atTop);
      }

      if (footerRef.current) {
        footerRef.current.classList.toggle('scrolled', !atBottom);
      }
    };

    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll();
    }

    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <Modal
        closeModal={closeModal}
        className={'pricebanner-view-modal'}
      >

        <div ref={headerRef} className='view-modal-header'>
          <h3>
            <strong style={{ color: 'black' }}>{translate('price.banner.params.view.title')}</strong>
          </h3>
        </div>


        <div ref={containerRef} className='view-container'>
          <div className="content-wrapper">
            <div className="row-pricebanner-modal">
              <div className='column-pricebanner-modal'>
                <p className='item'>{translate('price.banner.stores')}</p>
                <div ref={storesScrollRef} className='stores-scroll-container'>
                  <p className='sub-item'>{renderBranchNames()}</p>
                </div>
              </div>

              <div className='column-pricebanner-modal'>
                <p className='item'>{translate('priceBannerHistoryAdapter.dynamic')}</p>
                <p className='sub-item'>{dynamic?.dsDynamic || '-'}</p>
              </div>

            </div>

            <div className="row-pricebanner-modal" style={{ marginTop: "-10px", marginBottom: "-8px" }}>
              <div className="column-pricebanner-modal">
                <p className='item'>{translate('config.pricebannerConfig.tableHeader.dsCard')}</p>
                <p className='sub-item'>{capitalizeFirst(dsCard)}</p>
              </div>

              <div className='column-pricebanner-modal'>
                <p className='item'>{translate('price.banner.period')}</p>
                <p className='sub-item'>{`${formatDate(dtStart)} - ${formatDate(dtEnd)}`} </p>
              </div>
            </div>

          </div>

          <hr style={{ height: "2px" }}></hr>

          <table className='table-view'>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th style={{ width: '5%' }}>{translate('config.pricebannerConfig.view.ranges')}</th>
                <th style={{ width: '18%' }}>{translate('config.pricebannerConfig.form.cdParcelLabel')}</th>
                <th style={{ width: '21%' }}>{translate('priceBannerHistoryAdapter.dynamic')}</th>
                <th style={{ width: '18%' }}>{translate('config.pricebannerConfig.view.valueLimit')}</th>
              </tr>
            </thead>

            <tbody>
              {parcelRangeList.length === 0 ? (
                <tr>
                  <td colSpan={4}>{translate('price.banner.params.table.no.range')}</td>
                </tr>
              ) : (
                parcelRangeList.map((range, idx) => (
                  <tr key={idx}>
                    <td>{`Faixa ${idx + 1}`}</td>
                    <td> {range.cdParcel === 1 ? `${range.dsParcel} (sem parcelamento)` : range.dsParcel || '-'}</td>
                    <td>{range.dynamic?.dsDynamic || '-'}</td>
                    <td>
                      {`${formatCurrency(range.prMinValueFormatted)} - ${formatCurrency(range.prMaxValueFormatted)}`}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div ref={footerRef} className='mission-block-new-modal-actions view-modal-footer'>
          <button
            onClick={() => { openEditModal(param) }}
            className='mission-save'
          >
            {translate('edit_button_label')}
          </button>

          <button
            onClick={() => { closeModal() }}
            className='mission-cancel'
          >
            {translate('close_button_label')}

          </button>


        </div>
      </Modal>
    </>

  )
}

ViewModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  param: PropTypes.object.isRequired,
  openEditModal: PropTypes.func.isRequired
}

export default ViewModal
